import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Calendar, Plus, Upload, X, AlertCircle, CheckSquare, Pencil, Eye, Send, CheckCircle2, Circle, Trash2, ChevronUp, ChevronDown, Archive, HelpCircle, FileText, Download, Paperclip, Settings, Copy, ExternalLink } from "lucide-react";
import jsPDF from 'jspdf';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TaskAssigneeSelector } from "@/components/TaskAssigneeSelector";

interface Goal {
  id: string;
  title: string;
  details: string | null;
  target_amount: number;
  current_amount: number;
  target_date: string | null;
  status: string;
  priority: number;
  show_on_fundraising: boolean;
  image_url: string | null;
  submitted_by_email: string | null;
  rejection_reason: string | null;
  requester: string | null;
  allocated_funds?: number;
}

interface Topic {
  title: string;
  details: string;
}

interface Meeting {
  id: string;
  title: string;
  meeting_date: string;
  location: string | null;
  meeting_type: string | null;
  custom_meeting_type: string | null;
  topics: Topic[];
  invited_members: string[];
  status: string;
  archived_at: string | null;
  sections?: MeetingSection[];
}

interface MeetingSection {
  id: string;
  title: string;
  content: string | null;
  section_order: number;
}

interface Member {
  id: string;
  full_name: string;
  email: string;
  user_id: string | null;
  user_roles: { role: string }[];
}

interface TopicAction {
  description: string;
  assignedTo: string[];
}

interface TopicMinutes {
  topicTitle: string;
  minutes: string;
  actions: TopicAction[];
}

interface Task {
  id: string;
  title: string;
  description: string | null;
  task_type: 'personal' | 'assigned' | 'general';
  assigned_to: string | null;
  status: 'pending' | 'completed';
  created_at: string;
  created_by: string | null;
  assigned_members?: Member[];
  role_context?: string | null;
  task_group_id?: string | null;
  assigned_role?: string | null;
}

interface Alert {
  id: string;
  title: string;
  due_date: string;
  alert_date: string;
  status: string;
  created_at: string;
}

interface StaffSubmission {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  email: string | null;
  title: string;
  details: string;
  estimated_cost: string | null;
  file_url: string | null;
  status: string;
  created_at: string;
  pta_id: string;
  rejection_reason: string | null;
}

function SortableGoal({ goal, onStatusChange, onReject, onEdit, onViewDetails, onDelete, onMoveUp, onMoveDown, isFirst, isLast, allGoals }: { 
  goal: Goal; 
  onStatusChange: (id: string, status: string) => void;
  onReject: (id: string, reason: string) => void;
  onEdit: (goal: Goal) => void;
  onViewDetails: (goal: Goal) => void;
  onDelete: (id: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  allGoals?: Goal[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: goal.id });
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getStatusOptions = () => {
    if (goal.status === 'submitted') return ['approved'];
    if (goal.status === 'approved') return ['up-next'];
    if (goal.status === 'up-next') return ['active', 'approved'];
    if (goal.status === 'active') return ['up-next', 'approved', 'archive'];
    return [];
  };

  const handleReject = () => {
    onReject(goal.id, rejectionReason);
    setShowRejectDialog(false);
    setRejectionReason("");
  };

  const isHighestPriority = isFirst && allGoals && allGoals.filter(g => g.status === goal.status).length > 1;

  return (
    <div ref={setNodeRef} style={style} className="p-4 border border-border rounded-lg hover:shadow-card transition-shadow bg-card">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium block">{goal.title}</span>
                {isHighestPriority && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Highest Priority</span>
                )}
              </div>
              {goal.requester && (
                <span className="text-xs text-muted-foreground block">Suggested by: {goal.requester}</span>
              )}
              {goal.submitted_by_email && (
                <span className="text-xs text-muted-foreground block">Submitted by: {goal.submitted_by_email}</span>
              )}
            </div>
            <div className="text-right">
              <span className="text-xs text-muted-foreground block">Target Amount:</span>
              <span className="text-sm font-semibold text-primary">£{goal.target_amount}</span>
            </div>
          </div>
          {goal.status === 'active' && (
            <div className="space-y-1 mb-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Progress</span>
                <span>{Math.round((goal.current_amount / goal.target_amount) * 100)}%</span>
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all" 
                  style={{ width: `${Math.min((goal.current_amount / goal.target_amount) * 100, 100)}%` }} 
                />
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Label className="text-xs text-muted-foreground">Status:</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs max-w-xs">
                      Change the goal's status to move it between sections: Submitted → Approved → Up Next → Active → Archive
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Select
              value={goal.status} 
              onValueChange={(val) => {
                if (val === 'rejected') {
                  setShowRejectDialog(true);
                } else {
                  onStatusChange(goal.id, val);
                }
              }}
            >
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getStatusOptions().map((status) => (
                  <SelectItem key={status} value={status} className="capitalize">
                    {status.replace('-', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 ml-auto">
              {!isFirst && onMoveUp && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onMoveUp}
                  className="h-8 w-8 p-0"
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
              )}
              {!isLast && onMoveDown && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onMoveDown}
                  className="h-8 w-8 p-0"
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              )}
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => onEdit(goal)}
                className="h-8 w-8 p-0"
                title="Edit goal"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => onViewDetails(goal)}
                className="h-8 w-8 p-0"
                title="View details"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => onDelete(goal.id)}
                className="h-8 w-8 p-0"
                title="Delete goal"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {!goal.show_on_fundraising && (
              <span className="text-xs text-muted-foreground">(Hidden from parents)</span>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Goal</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this goal. The submitter will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleReject}>Reject Goal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ChairDashboard() {
  const { toast } = useToast();
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [isMeetingDialogOpen, setIsMeetingDialogOpen] = useState(false);
  const [isEditGoalDialogOpen, setIsEditGoalDialogOpen] = useState(false);
  const [isViewGoalDialogOpen, setIsViewGoalDialogOpen] = useState(false);
  const [isEditTaskDialogOpen, setIsEditTaskDialogOpen] = useState(false);
  const [isAddTaskDialogOpen, setIsAddTaskDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showOnFundraising, setShowOnFundraising] = useState(true);
  const [meetingType, setMeetingType] = useState("");
  const [customMeetingType, setCustomMeetingType] = useState("");
  const [topics, setTopics] = useState<Topic[]>([{ title: "", details: "" }]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [taskType, setTaskType] = useState<'assigned'>('assigned');
  const [taskAssignees, setTaskAssignees] = useState<string[]>([]);
  const [taskRoles, setTaskRoles] = useState<string[]>([]);
  const [commMessage, setCommMessage] = useState("");
  const [commRecipients, setCommRecipients] = useState<string[]>([]);
  const [commSubject, setCommSubject] = useState("");
  const [commAttachment, setCommAttachment] = useState<File | null>(null);
  const [commMeetingAttachment, setCommMeetingAttachment] = useState<string>("");
  const [showArchivedGoals, setShowArchivedGoals] = useState(false);
  const [showArchivedMeetings, setShowArchivedMeetings] = useState(false);
  const [archivedMeetings, setArchivedMeetings] = useState<Meeting[]>([]);
  const [ptaId, setPtaId] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [staffSubmissionLink, setStaffSubmissionLink] = useState<string>("");
  const [enableStaffSubmissions, setEnableStaffSubmissions] = useState(false);
  const [staffSubmissions, setStaffSubmissions] = useState<StaffSubmission[]>([]);
  const [isStaffSubmissionDialogOpen, setIsStaffSubmissionDialogOpen] = useState(false);
  const [selectedStaffSubmission, setSelectedStaffSubmission] = useState<StaffSubmission | null>(null);
  const [staffRejectionReason, setStaffRejectionReason] = useState("");
  const [isStaffRejectDialogOpen, setIsStaffRejectDialogOpen] = useState(false);
  
  // Minutes states (view only)
  const [isMinutesDialogOpen, setIsMinutesDialogOpen] = useState(false);
  const [minutesMeeting, setMinutesMeeting] = useState<Meeting | null>(null);
  const [savedMinutesContent, setSavedMinutesContent] = useState<string | null>(null);
  const [loadingMinutes, setLoadingMinutes] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPtaId();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);
  };

  useEffect(() => {
    if (ptaId) {
      fetchGoals();
      fetchMeetings();
      fetchMembers();
      fetchTasks();
      fetchAlerts();
      fetchStaffSubmissions();
    }
    
    // Update meeting statuses based on date
    const interval = setInterval(() => {
      updateMeetingStatuses();
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [ptaId]);

  const fetchPtaId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .single();
    
    if (!error && data) {
      setPtaId(data.pta_id);
      
      // Fetch staff submission settings
      const uniqueCode = data.pta_id.replace(/-/g, '').substring(0, 8);
      setStaffSubmissionLink(`${window.location.origin}/staff-request/${uniqueCode}`);
      
      const { data: ptaData } = await supabase
        .from('ptas')
        .select('enable_staff_submissions')
        .eq('id', data.pta_id)
        .single();
      
      if (ptaData) {
        setEnableStaffSubmissions(ptaData.enable_staff_submissions ?? false);
      }
    }
  };

  const updateMeetingStatuses = async () => {
    const now = new Date();
    const updates = meetings
      .filter(m => new Date(m.meeting_date) < now && m.status === 'upcoming')
      .map(m => 
        supabase
          .from('meetings')
          .update({ status: 'taken-place' })
          .eq('id', m.id)
      );
    
    if (updates.length > 0) {
      await Promise.all(updates);
      fetchMeetings();
    }
  };

  const fetchGoals = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    // Get user's PTA
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .single();
    
    if (!memberData?.pta_id) return;
    
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('pta_id', memberData.pta_id)
      .order('priority', { ascending: true });
    
    if (!error && data) {
      // Fetch allocations for each goal (filtered by PTA for multi-tenant security)
      const { data: allocations } = await supabase
        .from('fund_allocations')
        .select('goal_id, amount')
        .eq('pta_id', memberData.pta_id);
      
      const allocationMap = new Map();
      allocations?.forEach(a => {
        const current = allocationMap.get(a.goal_id) || 0;
        allocationMap.set(a.goal_id, current + Number(a.amount));
      });
      
      const enrichedGoals = data.map(g => ({
        ...g,
        allocated_funds: allocationMap.get(g.id) || 0
      }));
      
      setGoals(enrichedGoals);
    }
  };

  const fetchMeetings = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('meetings')
      .select(`
        *,
        sections:meeting_sections(*)
      `)
      .eq('pta_id', ptaId)
      .is('archived_at', null)
      .order('meeting_date', { ascending: false });
    
    if (!error && data) setMeetings(data as any);
  };

  const fetchArchivedMeetings = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('meetings')
      .select(`
        *,
        sections:meeting_sections(*)
      `)
      .eq('pta_id', ptaId)
      .not('archived_at', 'is', null)
      .order('archived_at', { ascending: false });
    
    if (!error && data) setArchivedMeetings(data as any);
  };

  const fetchMembers = async () => {
    if (!ptaId) return;
    const { data: membersData } = await supabase
      .from('members')
      .select('id, full_name, email, user_id')
      .eq('pta_id', ptaId)
      .eq('status', 'active');

    if (membersData) {
      const membersWithRoles = await Promise.all(
        membersData.map(async (m) => {
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', m.user_id || '');
          return { ...m, user_roles: roles || [] };
        })
      );
      setMembers(membersWithRoles);
    }
  };

  const fetchTasks = async () => {
    if (!ptaId) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Fetch tasks: 
    // 1. Tasks created by me from this dashboard (role_context = 'chair')
    // 2. Tasks assigned TO me (from any dashboard)
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('pta_id', ptaId)
      .or(`and(role_context.eq.chair,created_by.eq.${user.id}),assigned_to.eq.${user.id}`)
      .order('created_at', { ascending: false });

    if (!error && data) setTasks(data as any);
  };

  const fetchAlerts = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('alerts')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('status', 'active')
      .order('alert_date', { ascending: true });
    
    if (!error && data) setAlerts(data);
  };

  const fetchStaffSubmissions = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('staff_submissions')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    
    if (!error && data) setStaffSubmissions(data);
  };

  const sendStaffStatusEmail = async (submission: StaffSubmission, status: string, rejectionReason?: string) => {
    if (!submission.email) {
      console.log('No email provided for staff submission, skipping notification');
      return;
    }

    try {
      // Get PTA name
      const { data: ptaData } = await supabase
        .from('ptas')
        .select('name')
        .eq('id', ptaId)
        .single();
      
      const ptaName = ptaData?.name || 'Your PTA';
      const templateName = status === 'approved' ? 'staff_request_approved' : 'staff_request_rejected';
      
      const { data: { session } } = await supabase.auth.getSession();
      
      const dynamicData: Record<string, string> = {
        first_name: submission.first_name,
        title: submission.title,
        pta_name: ptaName,
      };
      
      if (status === 'rejected' && rejectionReason) {
        dynamicData.rejection_reason = rejectionReason;
      }

      await supabase.functions.invoke('send-email', {
        body: {
          templateName,
          recipients: submission.email,
          dynamicData,
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      
      console.log('Staff status email sent successfully');
    } catch (error) {
      console.error('Failed to send staff status email:', error);
    }
  };

  const handleStaffSubmissionStatusChange = async (submission: StaffSubmission, newStatus: string) => {
    if (newStatus === 'rejected') {
      setSelectedStaffSubmission(submission);
      setIsStaffRejectDialogOpen(true);
      return;
    }
    
    // For approval, also convert to goal with 'approved' status
    const { error: goalError } = await supabase.from('goals').insert({
      pta_id: ptaId,
      title: submission.title,
      details: submission.details,
      target_amount: parseFloat(submission.estimated_cost || '0'),
      current_amount: 0,
      status: 'approved',
      priority: goals.length,
      show_on_fundraising: false,
      requester: `${submission.first_name} ${submission.last_name} (${submission.role})`,
    });
    
    if (goalError) {
      toast({
        title: "Error",
        description: goalError.message,
        variant: "destructive",
      });
      return;
    }

    const { error: updateError } = await supabase
      .from('staff_submissions')
      .update({ status: 'approved' })
      .eq('id', submission.id);

    if (updateError) {
      toast({
        title: "Error",
        description: updateError.message,
        variant: "destructive",
      });
    } else {
      // Send approval email
      await sendStaffStatusEmail(submission, 'approved');
      
      toast({
        title: "Request approved",
        description: "The staff request has been approved and added to your goals.",
      });
      fetchGoals();
      fetchStaffSubmissions();
    }
  };

  const handleRejectStaffSubmission = async () => {
    if (!selectedStaffSubmission) return;
    
    const { error } = await supabase
      .from('staff_submissions')
      .update({ 
        status: 'rejected',
        rejection_reason: staffRejectionReason || null,
      })
      .eq('id', selectedStaffSubmission.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      // Send rejection email
      await sendStaffStatusEmail(selectedStaffSubmission, 'rejected', staffRejectionReason);
      
      toast({
        title: "Request rejected",
        description: "The staff funding request has been rejected.",
      });
      setIsStaffRejectDialogOpen(false);
      setSelectedStaffSubmission(null);
      setStaffRejectionReason("");
      fetchStaffSubmissions();
    }
  };

  const handleDeleteStaffSubmission = async (id: string) => {
    const { error } = await supabase
      .from('staff_submissions')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Request deleted",
        description: "The staff funding request has been removed.",
      });
      fetchStaffSubmissions();
    }
  };

  const handleCreateGoal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    // Get current user's PTA
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .single();
    
    if (!memberData?.pta_id) {
      toast({
        title: "Error",
        description: "Could not find your PTA association",
        variant: "destructive",
      });
      return;
    }
    
    let imageUrl = null;
    if (imageFile) {
      const fileName = `${Date.now()}_${imageFile.name}`;
      const { data, error: uploadError } = await supabase.storage
        .from('fundraising-images')
        .upload(fileName, imageFile);

      if (!uploadError && data) {
        const { data: { publicUrl } } = supabase.storage
          .from('fundraising-images')
          .getPublicUrl(data.path);
        imageUrl = publicUrl;
      }
    }

    const { error } = await supabase.from('goals').insert({
      title: formData.get('title') as string,
      requester: formData.get('requester') as string || null,
      details: formData.get('details') as string,
      target_amount: parseFloat(formData.get('target_amount') as string),
      target_date: formData.get('target_date') as string || null,
      show_on_fundraising: showOnFundraising,
      image_url: imageUrl,
      status: 'submitted',
      priority: goals.length,
      pta_id: memberData.pta_id,
      submitted_by: user.id,
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Goal created!",
        description: "Your fundraising goal has been added successfully.",
      });
      setIsGoalDialogOpen(false);
      setImageFile(null);
      setShowOnFundraising(true);
      fetchGoals();
    }
  };

  const handleCreateMeeting = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase.from('meetings').insert({
      title: formData.get('title') as string,
      meeting_date: formData.get('meeting_date') as string,
      location: formData.get('location') as string,
      meeting_type: meetingType === 'Other' ? null : meetingType,
      custom_meeting_type: meetingType === 'Other' ? customMeetingType : null,
      topics: topics.filter(t => t.title && t.details) as any,
      invited_members: selectedMembers,
      status: 'upcoming',
      created_by: user?.id,
      pta_id: ptaId,
    } as any);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Meeting created",
        description: "Meeting details have been saved and members will be notified.",
      });
      setIsMeetingDialogOpen(false);
      setMeetingType("");
      setCustomMeetingType("");
      setTopics([{ title: "", details: "" }]);
      setSelectedMembers([]);
      fetchMeetings();
    }
  };

  const handleUpdateMeeting = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedMeeting) return;
    
    const formData = new FormData(e.currentTarget);
    
    const { error } = await supabase
      .from('meetings')
      .update({
        title: formData.get('title') as string,
        meeting_date: formData.get('meeting_date') as string,
        location: formData.get('location') as string,
        meeting_type: meetingType === 'Other' ? null : meetingType,
        custom_meeting_type: meetingType === 'Other' ? customMeetingType : null,
        topics: topics.filter(t => t.title || t.details) as any,
        invited_members: selectedMembers,
      })
      .eq('id', selectedMeeting.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Meeting updated",
        description: "Meeting details have been saved.",
      });
      setIsMeetingDialogOpen(false);
      setSelectedMeeting(null);
      setMeetingType("");
      setCustomMeetingType("");
      setTopics([{ title: "", details: "" }]);
      setSelectedMembers([]);
      fetchMeetings();
    }
  };

  const handleGoalStatusChange = async (goalId: string, newStatus: string) => {
    const { error } = await supabase
      .from('goals')
      .update({ status: newStatus })
      .eq('id', goalId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Status updated",
        description: `Goal moved to ${newStatus.replace('-', ' ')} section.`,
      });
      await fetchGoals();
    }
  };

  const handleGoalReject = async (goalId: string, reason: string) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;

    const { error } = await supabase
      .from('goals')
      .update({ 
        status: 'rejected',
        rejection_reason: reason 
      })
      .eq('id', goalId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      if (goal.submitted_by_email) {
        toast({
          title: "Goal rejected",
          description: `Email notification sent to ${goal.submitted_by_email} with reason`,
        });
      }
      fetchGoals();
    }
  };

  const handleGoalDelete = async (goalId: string) => {
    const { error } = await supabase
      .from('goals')
      .delete()
      .eq('id', goalId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Goal deleted",
        description: "The goal has been removed",
      });
      fetchGoals();
    }
  };

  const handleDragEnd = async (event: DragEndEvent, status: string) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const statusGoals = goals.filter(g => g.status === status);
    const oldIndex = statusGoals.findIndex((g) => g.id === active.id);
    const newIndex = statusGoals.findIndex((g) => g.id === over.id);

    const newOrder = arrayMove(statusGoals, oldIndex, newIndex);
    
    // Update priorities in database
    const updates = newOrder.map((goal, index) => 
      supabase
        .from('goals')
        .update({ priority: index })
        .eq('id', goal.id)
    );

    await Promise.all(updates);
    
    toast({
      title: "Priority updated",
      description: "Goal priority has been reordered and saved.",
    });
    
    fetchGoals();
  };

  const handleArchiveMeeting = async (meetingId: string) => {
    const meeting = meetings.find(m => m.id === meetingId);
    if (!meeting) return;

    const hasMinutes = meeting.sections && meeting.sections.length > 0;
    
    if (!hasMinutes) {
      const confirmed = window.confirm("No minutes have been added to this meeting. Are you sure you want to archive it?");
      if (!confirmed) return;
    }

    const { error } = await supabase
      .from('meetings')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', meetingId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Meeting archived",
        description: "The meeting has been moved to archived meetings.",
      });
      fetchMeetings();
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    if (taskAssignees.length === 0 && taskRoles.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one role or person to assign the task to.",
        variant: "destructive",
      });
      return;
    }

    const tasksToInsert: any[] = [];
    const taskGroupId = crypto.randomUUID(); // Group ID for role-based tasks
    
    // If "ME" is selected, create a personal task
    if (taskAssignees.includes('me')) {
      tasksToInsert.push({
        title: newTaskTitle,
        description: newTaskDescription || null,
        task_type: 'personal',
        assigned_to: user.id,
        created_by: user.id,
        status: 'pending',
        pta_id: ptaId,
        role_context: 'chair',
      });
    }
    
    // For individual members, create assigned tasks (no group)
    const otherAssignees = taskAssignees.filter(id => id !== 'me');
    otherAssignees.forEach(assigneeId => {
      tasksToInsert.push({
        title: newTaskTitle,
        description: newTaskDescription || null,
        task_type: 'assigned',
        assigned_to: assigneeId,
        created_by: user.id,
        status: 'pending',
        pta_id: ptaId,
        role_context: 'chair',
      });
    });

    // For roles, find all members with those roles and create tasks for them (with group)
    if (taskRoles.length > 0) {
      const { data: roleMembers } = await supabase
        .from('user_roles')
        .select('user_id, role');
      
      if (roleMembers) {
        const assignedRolesString = taskRoles.join(',');
        for (const role of taskRoles) {
          const usersWithRole = roleMembers.filter(rm => rm.role === role);
          for (const ur of usersWithRole) {
            // Find the member ID for this user in this PTA
            const member = members.find(m => m.user_id === ur.user_id);
            if (member && member.user_id && !otherAssignees.includes(member.user_id)) {
              // Avoid duplicates if member was also selected individually
              tasksToInsert.push({
                title: newTaskTitle,
                description: newTaskDescription || null,
                task_type: 'assigned',
                assigned_to: member.user_id,
                created_by: user.id,
                status: 'pending',
                pta_id: ptaId,
                role_context: 'chair',
                task_group_id: taskGroupId,
                assigned_role: assignedRolesString,
              });
            }
          }
        }
      }
    }

    const { error } = await supabase.from('tasks').insert(tasksToInsert);
    
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Task added",
      description: tasksToInsert.length === 1 && taskAssignees.includes('me') 
        ? "Personal task created." 
        : `Task(s) assigned to ${tasksToInsert.length} recipient(s).`,
    });
    
    setNewTaskTitle("");
    setNewTaskDescription("");
    setTaskAssignees([]);
    setTaskRoles([]);
    setIsAddTaskDialogOpen(false);
    fetchTasks();
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    const { error } = await supabase
      .from('tasks')
      .update({ 
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null 
      })
      .eq('id', taskId);

    if (!error) {
      fetchTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (!error) {
      fetchTasks();
    }
  };

  const handleUpdateTask = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingTask) return;

    const { error } = await supabase
      .from('tasks')
      .update({
        title: newTaskTitle,
        description: newTaskDescription || null,
      })
      .eq('id', editingTask.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Task updated",
        description: "Task has been updated successfully.",
      });
      setIsEditTaskDialogOpen(false);
      setEditingTask(null);
      setNewTaskTitle("");
      setNewTaskDescription("");
      fetchTasks();
    }
  };

  const handleSendCommunication = async () => {
    if (!commMessage || commRecipients.length === 0 || !commSubject) {
      toast({
        title: "Error",
        description: "Please enter a subject, message and select at least one recipient.",
        variant: "destructive",
      });
      return;
    }

    // Here you would typically send emails via an edge function
    toast({
      title: "Communication sent",
      description: `Message sent to ${commRecipients.length} member(s).`,
    });
    
    setCommMessage("");
    setCommRecipients([]);
    setCommSubject("");
    setCommAttachment(null);
    setCommMeetingAttachment("");
  };

  const fetchMeetingMinutes = async (meetingId: string) => {
    setLoadingMinutes(true);
    const { data, error } = await supabase
      .from('meeting_sections')
      .select('content')
      .eq('meeting_id', meetingId)
      .eq('title', 'Meeting Minutes')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') {
      toast({
        title: "Error",
        description: "Failed to load minutes.",
        variant: "destructive",
      });
    }
    
    setSavedMinutesContent(data?.content || null);
    setLoadingMinutes(false);
  };

  const generateAgendaPDF = (meeting: Meeting) => {
    const doc = new jsPDF();
    const meetingDate = new Date(meeting.meeting_date);
    const formattedDate = meetingDate.toLocaleDateString('en-GB');
    
    // Title
    doc.setFontSize(18);
    doc.text('Meeting Agenda', 105, 20, { align: 'center' });
    
    // Meeting details
    doc.setFontSize(12);
    doc.text(`Meeting: ${meeting.title}`, 20, 40);
    doc.text(`Type: ${meeting.meeting_type || meeting.custom_meeting_type || 'General'}`, 20, 50);
    doc.text(`Date: ${meetingDate.toLocaleString()}`, 20, 60);
    if (meeting.location) {
      doc.text(`Location: ${meeting.location}`, 20, 70);
    }
    
    // Topics
    let yPos = meeting.location ? 90 : 80;
    doc.setFontSize(14);
    doc.text('Agenda Items:', 20, yPos);
    
    doc.setFontSize(11);
    if (meeting.topics && meeting.topics.length > 0) {
      meeting.topics.forEach((topic, idx) => {
        yPos += 12;
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(`${idx + 1}. ${topic.title}`, 25, yPos);
        if (topic.details) {
          yPos += 8;
          const lines = doc.splitTextToSize(topic.details, 160);
          lines.forEach((line: string) => {
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            doc.text(line, 30, yPos);
            yPos += 6;
          });
        }
      });
    } else {
      yPos += 12;
      doc.text('No agenda items specified.', 25, yPos);
    }
    
    // Generate filename
    const filename = `${meeting.title.replace(/[^a-z0-9]/gi, '-')}-${formattedDate.replace(/\//g, '-')}.pdf`;
    doc.save(filename);
  };

  const handleMoveGoalUp = async (goal: Goal) => {
    const statusGoals = goals.filter(g => g.status === goal.status).sort((a, b) => a.priority - b.priority);
    const index = statusGoals.findIndex(g => g.id === goal.id);
    if (index <= 0) return;

    const goalAbove = statusGoals[index - 1];
    await supabase.from('goals').update({ priority: goalAbove.priority }).eq('id', goal.id);
    await supabase.from('goals').update({ priority: goal.priority }).eq('id', goalAbove.id);
    fetchGoals();
  };

  const handleMoveGoalDown = async (goal: Goal) => {
    const statusGoals = goals.filter(g => g.status === goal.status).sort((a, b) => a.priority - b.priority);
    const index = statusGoals.findIndex(g => g.id === goal.id);
    if (index === -1 || index >= statusGoals.length - 1) return;

    const goalBelow = statusGoals[index + 1];
    await supabase.from('goals').update({ priority: goalBelow.priority }).eq('id', goal.id);
    await supabase.from('goals').update({ priority: goal.priority }).eq('id', goalBelow.id);
    fetchGoals();
  };

  const activeGoals = goals.filter(g => g.status === 'active');
  const upNextGoals = goals.filter(g => g.status === 'up-next');
  const approvedGoals = goals.filter(g => g.status === 'approved');
  const submittedGoals = goals.filter(g => g.status === 'submitted');
  const archivedGoals = goals.filter(g => g.status === 'archive');

  const personalTasks = tasks.filter(t => t.task_type === 'personal' || t.task_type === 'assigned');

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 space-y-6">
        <div className="flex items-center justify-between">
          <div>
        <h1 className="text-4xl font-bold text-foreground">
          Chair Dashboard
        </h1>
            <p className="text-muted-foreground mt-2">Manage goals, meetings, and communications</p>
          </div>
        </div>

        <Tabs defaultValue="goals" className="space-y-4">
          <TabsList className="inline-flex h-auto items-center justify-start gap-8 border-b border-border w-full">
            <TabsTrigger value="goals" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Goals</TabsTrigger>
            <TabsTrigger value="meetings" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Meetings</TabsTrigger>
            <TabsTrigger value="tasks" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Tasks</TabsTrigger>
            <TabsTrigger value="communications" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Communications</TabsTrigger>
            <TabsTrigger value="alerts" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Alerts</TabsTrigger>
            <TabsTrigger value="settings" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Goals Tab */}
          <TabsContent value="goals" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Manage Fundraising Goals</CardTitle>
                    <CardDescription>Manage fundraising goals and their status.</CardDescription>
                    <p className="text-sm text-muted-foreground mt-2">
                      Use the up/down arrows to adjust goal priority within each section.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowArchivedGoals(!showArchivedGoals)}>
                      <Archive className="h-4 w-4 mr-2" />
                      {showArchivedGoals ? 'Hide' : 'View'} Archived
                    </Button>
                    <Dialog open={isGoalDialogOpen} onOpenChange={setIsGoalDialogOpen}>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Goal
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Create New Goal</DialogTitle>
                        <DialogDescription>Add a new fundraising goal</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateGoal} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="title">Goal Title</Label>
                          <Input id="title" name="title" placeholder="e.g., New Playground Equipment" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="requester">Requester (Optional)</Label>
                          <Input id="requester" name="requester" placeholder="Person who suggested this idea" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="target_amount">Target Amount (£)</Label>
                          <Input id="target_amount" name="target_amount" type="number" step="0.01" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="target_date">Target Date (Optional)</Label>
                          <Input id="target_date" name="target_date" type="date" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="details">Details</Label>
                          <Textarea id="details" name="details" rows={3} placeholder="Describe the goal..." />
                        </div>
                        <div className="space-y-2">
                          <Label>Image (Optional)</Label>
                          <div
                            className={`border-2 border-dashed rounded-lg p-6 text-center ${
                              isDragging ? 'border-primary bg-primary/10' : 'border-border'
                            }`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setIsDragging(true);
                            }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={(e) => {
                              e.preventDefault();
                              setIsDragging(false);
                              const file = e.dataTransfer.files[0];
                              if (file && file.type.startsWith('image/')) {
                                setImageFile(file);
                              }
                            }}
                          >
                            <input
                              type="file"
                              id="image-upload"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) setImageFile(file);
                              }}
                            />
                            <label htmlFor="image-upload" className="cursor-pointer">
                              {imageFile ? (
                                <div className="space-y-2">
                                  <p className="text-sm font-medium">{imageFile.name}</p>
                                  <Button type="button" variant="outline" size="sm" onClick={() => setImageFile(null)}>
                                    <X className="h-4 w-4 mr-1" />
                                    Remove
                                  </Button>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">
                                    Drag and drop or click to upload
                                  </p>
                                </div>
                              )}
                            </label>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="show-on-fundraising"
                            checked={showOnFundraising}
                            onCheckedChange={setShowOnFundraising}
                          />
                          <Label htmlFor="show-on-fundraising">Show on fundraising page</Label>
                        </div>
                        <div className="flex justify-end gap-2">
                          <DialogClose asChild>
                            <Button type="button" variant="outline">Cancel</Button>
                          </DialogClose>
                          <Button type="submit">Create Goal</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Active Goals */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    Active Goals
                  </h3>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd(e, 'active')}
                  >
                    <SortableContext items={activeGoals.map(g => g.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {activeGoals.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No active goals</p>
                        ) : (
                          activeGoals.map((goal, idx) => (
                            <SortableGoal
                              key={goal.id}
                              goal={goal}
                              onStatusChange={handleGoalStatusChange}
                              onReject={handleGoalReject}
                              onDelete={handleGoalDelete}
                              onEdit={(g) => {
                                setSelectedGoal(g);
                                setIsEditGoalDialogOpen(true);
                              }}
                              onViewDetails={(g) => {
                                setSelectedGoal(g);
                                setIsViewGoalDialogOpen(true);
                              }}
                              onMoveUp={() => handleMoveGoalUp(goal)}
                              onMoveDown={() => handleMoveGoalDown(goal)}
                              isFirst={idx === 0}
                              isLast={idx === activeGoals.length - 1}
                              allGoals={activeGoals}
                            />
                          ))
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Up Next Goals */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                    Up Next Goals
                  </h3>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd(e, 'up-next')}
                  >
                    <SortableContext items={upNextGoals.map(g => g.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {upNextGoals.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No up next goals</p>
                        ) : (
                          upNextGoals.map((goal, idx) => (
                            <SortableGoal
                              key={goal.id}
                              goal={goal}
                              onStatusChange={handleGoalStatusChange}
                              onReject={handleGoalReject}
                              onDelete={handleGoalDelete}
                              onEdit={(g) => {
                                setSelectedGoal(g);
                                setIsEditGoalDialogOpen(true);
                              }}
                              onViewDetails={(g) => {
                                setSelectedGoal(g);
                                setIsViewGoalDialogOpen(true);
                              }}
                              onMoveUp={() => handleMoveGoalUp(goal)}
                              onMoveDown={() => handleMoveGoalDown(goal)}
                              isFirst={idx === 0}
                              isLast={idx === upNextGoals.length - 1}
                              allGoals={upNextGoals}
                            />
                          ))
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Approved Goals */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <CheckSquare className="h-5 w-5 text-purple-600" />
                    Approved Goals
                  </h3>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd(e, 'approved')}
                  >
                    <SortableContext items={approvedGoals.map(g => g.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {approvedGoals.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No approved goals</p>
                        ) : (
                          approvedGoals.map((goal, idx) => (
                            <SortableGoal
                              key={goal.id}
                              goal={goal}
                              onStatusChange={handleGoalStatusChange}
                              onReject={handleGoalReject}
                              onDelete={handleGoalDelete}
                              onEdit={(g) => {
                                setSelectedGoal(g);
                                setIsEditGoalDialogOpen(true);
                              }}
                              onViewDetails={(g) => {
                                setSelectedGoal(g);
                                setIsViewGoalDialogOpen(true);
                              }}
                              onMoveUp={() => handleMoveGoalUp(goal)}
                              onMoveDown={() => handleMoveGoalDown(goal)}
                              isFirst={idx === 0}
                              isLast={idx === approvedGoals.length - 1}
                              allGoals={approvedGoals}
                            />
                          ))
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Submitted Goals */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Submitted Goals
                  </h3>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd(e, 'submitted')}
                  >
                    <SortableContext items={submittedGoals.map(g => g.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {submittedGoals.length === 0 && staffSubmissions.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No submitted goals</p>
                        ) : (
                          <>
                            {/* Staff Submissions */}
                            {staffSubmissions.map((submission) => (
                              <div key={`staff-${submission.id}`} className="p-4 border border-border rounded-lg hover:shadow-card transition-shadow bg-card">
                                <div className="flex items-start gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-medium block">{submission.title}</span>
                                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300">Staff Request</span>
                                          {submission.file_url && (
                                            <a 
                                              href={submission.file_url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="text-xs text-primary hover:underline flex items-center gap-1"
                                            >
                                              <Paperclip className="h-3 w-3" />
                                              Attachment
                                            </a>
                                          )}
                                        </div>
                                        <span className="text-xs text-muted-foreground block">
                                          Requested by: {submission.first_name} {submission.last_name} ({submission.role})
                                        </span>
                                      </div>
                                      <div className="text-right">
                                        <span className="text-xs text-muted-foreground block">Estimated Cost:</span>
                                        <span className="text-sm font-semibold text-primary">
                                          £{parseFloat(submission.estimated_cost || '0').toLocaleString()}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <div className="flex items-center gap-1">
                                        <Label className="text-xs text-muted-foreground">Status:</Label>
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p className="text-xs max-w-xs">
                                                Approved requests are converted to goals. Rejected requests will notify the requester.
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      </div>
                                      <Select
                                        value="pending"
                                        onValueChange={(val) => handleStaffSubmissionStatusChange(submission, val)}
                                      >
                                        <SelectTrigger className="w-[140px] h-8">
                                          <SelectValue placeholder="Pending" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="approved">Approved</SelectItem>
                                          <SelectItem value="rejected">Rejected</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <div className="flex gap-2 ml-auto">
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          onClick={() => {
                                            setSelectedStaffSubmission(submission);
                                            setIsStaffSubmissionDialogOpen(true);
                                          }}
                                          className="h-8 w-8 p-0"
                                          title="View details"
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          onClick={() => handleDeleteStaffSubmission(submission.id)}
                                          className="h-8 w-8 p-0"
                                          title="Delete request"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            
                            {/* Regular Submitted Goals */}
                            {submittedGoals.map((goal, idx) => (
                              <SortableGoal
                                key={goal.id}
                                goal={goal}
                                onStatusChange={handleGoalStatusChange}
                                onReject={handleGoalReject}
                                onDelete={handleGoalDelete}
                                onEdit={(g) => {
                                  setSelectedGoal(g);
                                  setIsEditGoalDialogOpen(true);
                                }}
                                onViewDetails={(g) => {
                                  setSelectedGoal(g);
                                  setIsViewGoalDialogOpen(true);
                                }}
                                onMoveUp={() => handleMoveGoalUp(goal)}
                                onMoveDown={() => handleMoveGoalDown(goal)}
                                isFirst={idx === 0}
                                isLast={idx === submittedGoals.length - 1}
                                allGoals={submittedGoals}
                              />
                            ))}
                          </>
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Archived Goals */}
                {showArchivedGoals && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Archive className="h-5 w-5 text-gray-600" />
                      Archived Goals
                    </h3>
                    <div className="space-y-2">
                      {archivedGoals.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No archived goals</p>
                      ) : (
                        archivedGoals.map((goal, idx) => (
                          <SortableGoal
                            key={goal.id}
                            goal={goal}
                            onStatusChange={handleGoalStatusChange}
                            onReject={handleGoalReject}
                            onDelete={handleGoalDelete}
                            onEdit={(g) => {
                              setSelectedGoal(g);
                              setIsEditGoalDialogOpen(true);
                            }}
                            onViewDetails={(g) => {
                              setSelectedGoal(g);
                              setIsViewGoalDialogOpen(true);
                            }}
                            onMoveUp={() => handleMoveGoalUp(goal)}
                            onMoveDown={() => handleMoveGoalDown(goal)}
                            isFirst={idx === 0}
                            isLast={idx === archivedGoals.length - 1}
                            allGoals={archivedGoals}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* View Goal Details Dialog */}
            <Dialog open={isViewGoalDialogOpen} onOpenChange={setIsViewGoalDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Goal Details</DialogTitle>
                </DialogHeader>
                {selectedGoal && (
                  <div className="space-y-4">
                    {selectedGoal.image_url && (
                      <img src={selectedGoal.image_url} alt={selectedGoal.title} className="w-full rounded-lg" />
                    )}
                    <div>
                      <h3 className="font-semibold">{selectedGoal.title}</h3>
                      {selectedGoal.requester && (
                        <p className="text-sm text-muted-foreground">Suggested by: {selectedGoal.requester}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <p><span className="font-medium">Target Amount:</span> £{selectedGoal.target_amount}</p>
                      <p><span className="font-medium">Current Amount:</span> £{selectedGoal.current_amount}</p>
                      {selectedGoal.target_date && (
                        <p><span className="font-medium">Target Date:</span> {new Date(selectedGoal.target_date).toLocaleDateString()}</p>
                      )}
                      <p><span className="font-medium">Status:</span> {selectedGoal.status}</p>
                    </div>
                    {selectedGoal.details && (
                      <div>
                        <p className="font-medium mb-1">Details:</p>
                        <p className="text-sm">{selectedGoal.details}</p>
                      </div>
                    )}
                    <Button onClick={() => setIsViewGoalDialogOpen(false)}>Close</Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Edit Goal Dialog */}
            <Dialog open={isEditGoalDialogOpen} onOpenChange={setIsEditGoalDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Edit Goal</DialogTitle>
                  <DialogDescription>Update goal details and settings</DialogDescription>
                </DialogHeader>
                {selectedGoal && (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    
                    const { error } = await supabase
                      .from('goals')
                      .update({
                        title: formData.get('title') as string,
                        requester: formData.get('requester') as string || null,
                        target_amount: parseFloat(formData.get('target_amount') as string),
                        target_date: formData.get('target_date') as string || null,
                        details: formData.get('details') as string || null,
                        show_on_fundraising: formData.get('show_on_fundraising') === 'on',
                      })
                      .eq('id', selectedGoal.id);

                    if (error) {
                      toast({
                        title: "Error",
                        description: error.message,
                        variant: "destructive",
                      });
                    } else {
                      toast({
                        title: "Goal updated",
                        description: "The goal has been updated successfully.",
                      });
                      setIsEditGoalDialogOpen(false);
                      setSelectedGoal(null);
                      fetchGoals();
                    }
                  }} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-title">Goal Title</Label>
                      <Input id="edit-title" name="title" defaultValue={selectedGoal.title} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-requester">Requester (Optional)</Label>
                      <Input id="edit-requester" name="requester" defaultValue={selectedGoal.requester || ''} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-target-amount">Target Amount (£)</Label>
                      <Input id="edit-target-amount" name="target_amount" type="number" step="0.01" defaultValue={selectedGoal.target_amount} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-target-date">Target Date (Optional)</Label>
                      <Input id="edit-target-date" name="target_date" type="date" defaultValue={selectedGoal.target_date || ''} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-details">Details</Label>
                      <Textarea id="edit-details" name="details" rows={3} defaultValue={selectedGoal.details || ''} />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="edit-show-on-fundraising" name="show_on_fundraising" defaultChecked={selectedGoal.show_on_fundraising} />
                      <Label htmlFor="edit-show-on-fundraising">Show on fundraising page</Label>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => {
                        setIsEditGoalDialogOpen(false);
                        setSelectedGoal(null);
                      }}>Cancel</Button>
                      <Button type="submit">Update Goal</Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {/* View Staff Submission Dialog */}
            <Dialog open={isStaffSubmissionDialogOpen} onOpenChange={setIsStaffSubmissionDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Staff Funding Request</DialogTitle>
                  <DialogDescription>
                    Request submitted on {selectedStaffSubmission && new Date(selectedStaffSubmission.created_at).toLocaleDateString()}
                  </DialogDescription>
                </DialogHeader>
                {selectedStaffSubmission && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300">Staff Request</span>
                      </div>
                      <h3 className="font-semibold text-lg">{selectedStaffSubmission.title}</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Requested by</p>
                        <p className="font-medium">{selectedStaffSubmission.first_name} {selectedStaffSubmission.last_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Role</p>
                        <p className="font-medium">{selectedStaffSubmission.role}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Estimated Cost</p>
                        <p className="font-medium text-primary">£{parseFloat(selectedStaffSubmission.estimated_cost || '0').toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Status</p>
                        <p className="font-medium capitalize">{selectedStaffSubmission.status}</p>
                      </div>
                    </div>
                    {selectedStaffSubmission.details && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Details</p>
                        <p className="text-sm bg-muted p-3 rounded">{selectedStaffSubmission.details}</p>
                      </div>
                    )}
                    {selectedStaffSubmission.file_url && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Attachment</p>
                        <a 
                          href={selectedStaffSubmission.file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          <Paperclip className="h-4 w-4" />
                          View Attachment
                        </a>
                      </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={() => {
                        setIsStaffSubmissionDialogOpen(false);
                        setSelectedStaffSubmission(null);
                      }}>Close</Button>
                      <Button variant="destructive" onClick={() => {
                        setIsStaffSubmissionDialogOpen(false);
                        setIsStaffRejectDialogOpen(true);
                      }}>Reject</Button>
                      <Button onClick={() => {
                        handleStaffSubmissionStatusChange(selectedStaffSubmission, 'approved');
                        setIsStaffSubmissionDialogOpen(false);
                        setSelectedStaffSubmission(null);
                      }}>Approve</Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Reject Staff Submission Dialog */}
            <Dialog open={isStaffRejectDialogOpen} onOpenChange={setIsStaffRejectDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reject Staff Request</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to reject this funding request?
                  </DialogDescription>
                </DialogHeader>
                {selectedStaffSubmission && (
                  <div className="space-y-4">
                    <div className="p-3 bg-muted rounded">
                      <p className="font-medium">{selectedStaffSubmission.title}</p>
                      <p className="text-sm text-muted-foreground">
                        From: {selectedStaffSubmission.first_name} {selectedStaffSubmission.last_name}
                      </p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => {
                        setIsStaffRejectDialogOpen(false);
                        setSelectedStaffSubmission(null);
                        setStaffRejectionReason("");
                      }}>Cancel</Button>
                      <Button variant="destructive" onClick={handleRejectStaffSubmission}>
                        Reject Request
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Meetings Tab */}
          <TabsContent value="meetings" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Meetings</CardTitle>
                    <CardDescription>Schedule and manage committee meetings</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => {
                      fetchArchivedMeetings();
                      setShowArchivedMeetings(true);
                    }}>
                      <Archive className="h-4 w-4 mr-2" />
                      View Archive
                    </Button>
                    <Dialog open={isMeetingDialogOpen} onOpenChange={(open) => {
                      setIsMeetingDialogOpen(open);
                      if (!open) {
                        setSelectedMeeting(null);
                        setMeetingType("");
                        setCustomMeetingType("");
                        setTopics([{ title: "", details: "" }]);
                        setSelectedMembers([]);
                      } else if (selectedMeeting) {
                        // Pre-populate form when editing
                        setMeetingType(selectedMeeting.meeting_type || (selectedMeeting.custom_meeting_type ? 'Other' : ''));
                        setCustomMeetingType(selectedMeeting.custom_meeting_type || '');
                        setTopics(selectedMeeting.topics?.length > 0 ? selectedMeeting.topics : [{ title: "", details: "" }]);
                        setSelectedMembers(selectedMeeting.invited_members || []);
                      }
                    }}>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Meeting
                        </Button>
                      </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{selectedMeeting ? 'Edit Meeting' : 'Create New Meeting'}</DialogTitle>
                        <DialogDescription>{selectedMeeting ? 'Update meeting details' : 'Schedule a committee meeting'}</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={selectedMeeting ? handleUpdateMeeting : handleCreateMeeting} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="meeting_title">Meeting Title</Label>
                          <Input id="meeting_title" name="title" defaultValue={selectedMeeting?.title || ''} required key={selectedMeeting?.id || 'new'} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="meeting_type">Meeting Type</Label>
                          <Select value={meetingType} onValueChange={setMeetingType} required>
                            <SelectTrigger>
                              <SelectValue placeholder="Select meeting type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AGM">AGM</SelectItem>
                              <SelectItem value="EGM">EGM</SelectItem>
                              <SelectItem value="Committee">Committee</SelectItem>
                              <SelectItem value="Event Planning">Event Planning</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {meetingType === 'Other' && (
                          <div className="space-y-2">
                            <Label htmlFor="custom_type">Custom Meeting Type</Label>
                            <Input
                              id="custom_type"
                              value={customMeetingType}
                              onChange={(e) => setCustomMeetingType(e.target.value)}
                              placeholder="Enter custom meeting type"
                              required
                            />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label htmlFor="meeting_date">Meeting Date & Time</Label>
                          <Input 
                            id="meeting_date" 
                            name="meeting_date" 
                            type="datetime-local" 
                            defaultValue={selectedMeeting ? new Date(selectedMeeting.meeting_date).toISOString().slice(0, 16) : ''} 
                            required 
                            key={`date-${selectedMeeting?.id || 'new'}`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="location">Location</Label>
                          <Input id="location" name="location" defaultValue={selectedMeeting?.location || ''} key={`loc-${selectedMeeting?.id || 'new'}`} />
                        </div>
                        <div className="space-y-2">
                          <Label>Topics</Label>
                          {topics.map((topic, index) => (
                            <div key={index} className="space-y-2 p-3 border rounded">
                              <Input
                                placeholder="Topic title"
                                value={topic.title}
                                onChange={(e) => {
                                  const newTopics = [...topics];
                                  newTopics[index].title = e.target.value;
                                  setTopics(newTopics);
                                }}
                              />
                              <Textarea
                                placeholder="Topic details"
                                value={topic.details}
                                onChange={(e) => {
                                  const newTopics = [...topics];
                                  newTopics[index].details = e.target.value;
                                  setTopics(newTopics);
                                }}
                                rows={2}
                              />
                              {topics.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newTopics = topics.filter((_, i) => i !== index);
                                    setTopics(newTopics);
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setTopics([...topics, { title: "", details: "" }])}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Topic
                          </Button>
                        </div>
                        <div className="space-y-2">
                          <Label>Invite Members</Label>
                          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                            {members.map((member) => (
                              <div key={member.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`member-${member.id}`}
                                  checked={selectedMembers.includes(member.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedMembers([...selectedMembers, member.id]);
                                    } else {
                                      setSelectedMembers(selectedMembers.filter(id => id !== member.id));
                                    }
                                  }}
                                />
                                <Label htmlFor={`member-${member.id}`} className="text-sm cursor-pointer">
                                  {member.full_name}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <DialogClose asChild>
                            <Button type="button" variant="outline">Cancel</Button>
                          </DialogClose>
                          <Button type="submit">{selectedMeeting ? 'Update Meeting' : 'Create Meeting'}</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {meetings.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No meetings scheduled</p>
                  ) : (
                    meetings.map((meeting) => (
                      <div key={meeting.id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold">{meeting.title}</h3>
                            <p className="text-sm text-muted-foreground">
                              {meeting.meeting_type || meeting.custom_meeting_type}
                            </p>
                            <p className="text-sm">
                              {new Date(meeting.meeting_date).toLocaleString()}
                            </p>
                            {meeting.location && (
                              <p className="text-sm text-muted-foreground">{meeting.location}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              Status: {meeting.status}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                    onClick={() => {
                                      setMinutesMeeting(meeting);
                                      fetchMeetingMinutes(meeting.id);
                                      setIsMinutesDialogOpen(true);
                                    }}
                                  >
                                    <FileText className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Minutes</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => generateAgendaPDF(meeting)}
                              className="h-8 w-8 p-0"
                              title="Download agenda PDF"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedMeeting(meeting);
                                setIsMeetingDialogOpen(true);
                              }}
                              className="h-8 w-8 p-0"
                              title="Edit meeting"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleArchiveMeeting(meeting.id)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Archive className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Archive meeting</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="Delete meeting"
                              onClick={async () => {
                                if (confirm('Are you sure you want to delete this meeting?')) {
                                  const { error } = await supabase
                                    .from('meetings')
                                    .delete()
                                    .eq('id', meeting.id);
                                  
                                  if (error) {
                                    toast({
                                      title: "Error",
                                      description: error.message,
                                      variant: "destructive",
                                    });
                                  } else {
                                    toast({
                                      title: "Meeting deleted",
                                      description: "The meeting has been removed.",
                                    });
                                    fetchMeetings();
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Archived Meetings Dialog */}
            <Dialog open={showArchivedMeetings} onOpenChange={setShowArchivedMeetings}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Archived Meetings</DialogTitle>
                  <DialogDescription>View and manage archived meetings</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  {archivedMeetings.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No archived meetings</p>
                  ) : (
                    archivedMeetings.map((meeting) => (
                      <div key={meeting.id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold">{meeting.title}</h3>
                            <p className="text-sm text-muted-foreground">
                              {meeting.meeting_type || meeting.custom_meeting_type}
                            </p>
                            <p className="text-sm">
                              {new Date(meeting.meeting_date).toLocaleString()}
                            </p>
                            {meeting.location && (
                              <p className="text-sm text-muted-foreground">{meeting.location}</p>
                            )}
                            {meeting.archived_at && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Archived: {new Date(meeting.archived_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="View meeting details"
                              onClick={() => {
                                setSelectedMeeting(meeting);
                                setShowArchivedMeetings(false);
                                setIsMeetingDialogOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="Delete meeting"
                              onClick={async () => {
                                if (confirm('Are you sure you want to permanently delete this archived meeting?')) {
                                  const { error } = await supabase
                                    .from('meetings')
                                    .delete()
                                    .eq('id', meeting.id);
                                  
                                  if (error) {
                                    toast({
                                      title: "Error",
                                      description: error.message,
                                      variant: "destructive",
                                    });
                                  } else {
                                    toast({
                                      title: "Meeting deleted",
                                      description: "The archived meeting has been permanently removed.",
                                    });
                                    fetchArchivedMeetings();
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="space-y-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Tasks</CardTitle>
                  <CardDescription>Manage personal and assigned tasks</CardDescription>
                </div>
                <Dialog open={isAddTaskDialogOpen} onOpenChange={setIsAddTaskDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Task
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Task</DialogTitle>
                      <DialogDescription>Create a new task and assign it to members</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddTask} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-task-title">Task Title</Label>
                        <Input
                          id="new-task-title"
                          placeholder="Task title..."
                          value={newTaskTitle}
                          onChange={(e) => setNewTaskTitle(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-task-description">Description (Optional)</Label>
                        <Textarea
                          id="new-task-description"
                          placeholder="Description..."
                          value={newTaskDescription}
                          onChange={(e) => setNewTaskDescription(e.target.value)}
                          rows={3}
                        />
                      </div>
                      <TaskAssigneeSelector
                        ptaId={ptaId}
                        members={members}
                        selectedAssignees={taskAssignees}
                        selectedRoles={taskRoles}
                        onAssigneesChange={setTaskAssignees}
                        onRolesChange={setTaskRoles}
                        includeMe={true}
                      />
                      <div className="flex justify-end">
                        <Button type="submit">Add Task</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* My Tasks - includes personal tasks and tasks assigned TO me by others */}
                <div>
                  <h3 className="font-medium mb-2">My Tasks</h3>
                  <div className="space-y-2">
                    {personalTasks.filter(t => t.task_type === 'personal' || (t.task_type === 'assigned' && t.assigned_to === currentUser?.id && t.created_by !== currentUser?.id)).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No tasks</p>
                    ) : (
                      personalTasks.filter(t => t.task_type === 'personal' || (t.task_type === 'assigned' && t.assigned_to === currentUser?.id && t.created_by !== currentUser?.id)).map((task) => (
                        <div key={task.id} className="flex items-center gap-2 p-3 border rounded-lg">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleTask(task.id, task.status)}
                          >
                            {task.status === 'completed' ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            ) : (
                              <Circle className="h-5 w-5" />
                            )}
                          </Button>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                {task.title}
                              </p>
                              {task.created_by !== currentUser?.id && task.role_context && (
                                <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  From: {task.role_context.charAt(0).toUpperCase() + task.role_context.slice(1)}
                                </span>
                              )}
                            </div>
                            {task.description && (
                              <p className="text-sm text-muted-foreground">{task.description}</p>
                            )}
                          </div>
                          {task.created_by === currentUser?.id && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Edit task"
                                onClick={() => {
                                  setEditingTask(task);
                                  setNewTaskTitle(task.title);
                                  setNewTaskDescription(task.description || '');
                                  setIsEditTaskDialogOpen(true);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Delete task"
                                onClick={() => handleDeleteTask(task.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Assigned Tasks - tasks I created and assigned to others */}
                <div>
                  <h3 className="font-medium mb-2">Assigned Tasks</h3>
                  <div className="space-y-2">
                    {(() => {
                      const myAssignedTasks = personalTasks.filter(t => t.task_type === 'assigned' && t.created_by === currentUser?.id);
                      if (myAssignedTasks.length === 0) {
                        return <p className="text-sm text-muted-foreground">No assigned tasks</p>;
                      }
                      
                      // Group tasks by task_group_id, non-grouped tasks get their own entry
                      const groupedTasks: Record<string, Task[]> = {};
                      const individualTasks: Task[] = [];
                      
                      myAssignedTasks.forEach(task => {
                        if (task.task_group_id) {
                          if (!groupedTasks[task.task_group_id]) {
                            groupedTasks[task.task_group_id] = [];
                          }
                          groupedTasks[task.task_group_id].push(task);
                        } else {
                          individualTasks.push(task);
                        }
                      });
                      
                      const ROLE_NAMES: Record<string, string> = {
                        chair: 'Chair', secretary: 'Secretary', treasurer: 'Treasurer',
                        trustee: 'Trustee', events: 'Events', community: 'Community',
                        catering: 'Catering', assets: 'Assets', shop: 'Shop', school_liaison: 'School Liaison'
                      };
                      
                      return (
                        <>
                          {/* Grouped (role-based) tasks */}
                          {Object.entries(groupedTasks).map(([groupId, tasks]) => {
                            const firstTask = tasks[0];
                            const allCompleted = tasks.every(t => t.status === 'completed');
                            const completedCount = tasks.filter(t => t.status === 'completed').length;
                            const roleNames = firstTask.assigned_role?.split(',').map(r => ROLE_NAMES[r] || r).join(', ');
                            
                            return (
                              <div key={groupId} className="p-3 border rounded-lg">
                                <div className="flex items-start gap-2">
                                  <div className={`h-5 w-5 flex items-center justify-center mt-0.5 ${allCompleted ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {allCompleted ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className={`font-medium ${allCompleted ? 'line-through text-muted-foreground' : ''}`}>
                                        {firstTask.title}
                                      </p>
                                      <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                        {roleNames}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        ({completedCount}/{tasks.length} completed)
                                      </span>
                                    </div>
                                    {firstTask.description && (
                                      <p className="text-sm text-muted-foreground mt-1">{firstTask.description}</p>
                                    )}
                                    <div className="mt-2 flex flex-wrap gap-1">
                                      {tasks.map(task => {
                                        const member = members.find(m => m.user_id === task.assigned_to);
                                        return (
                                          <span 
                                            key={task.id} 
                                            className={`text-xs px-2 py-0.5 rounded ${task.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-secondary text-secondary-foreground'}`}
                                          >
                                            {member?.full_name || 'Unknown'} {task.status === 'completed' && '✓'}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    title="Delete all tasks in group"
                                    onClick={async () => {
                                      for (const task of tasks) {
                                        await handleDeleteTask(task.id);
                                      }
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* Individual tasks */}
                          {individualTasks.map((task) => {
                            const assignedMember = members.find(m => m.user_id === task.assigned_to);
                            return (
                              <div key={task.id} className="flex items-center gap-2 p-3 border rounded-lg">
                                <div className={`h-5 w-5 flex items-center justify-center ${task.status === 'completed' ? 'text-green-600' : 'text-muted-foreground'}`}>
                                  {task.status === 'completed' ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                      {task.title}
                                    </p>
                                    {task.status === 'completed' && (
                                      <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                        Completed
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    Assigned to: {assignedMember?.full_name || 'Unknown'}
                                  </p>
                                  {task.description && (
                                    <p className="text-sm text-muted-foreground">{task.description}</p>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  title="Edit task"
                                  onClick={() => {
                                    setEditingTask(task);
                                    setNewTaskTitle(task.title);
                                    setNewTaskDescription(task.description || '');
                                    setIsEditTaskDialogOpen(true);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  title="Delete task"
                                  onClick={() => handleDeleteTask(task.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Edit Task Dialog */}
            <Dialog open={isEditTaskDialogOpen} onOpenChange={(open) => {
              setIsEditTaskDialogOpen(open);
              if (!open) {
                setEditingTask(null);
                setNewTaskTitle("");
                setNewTaskDescription("");
              }
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Task</DialogTitle>
                  <DialogDescription>Update task details</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleUpdateTask} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-task-title">Task Title</Label>
                    <Input
                      id="edit-task-title"
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-task-description">Description (Optional)</Label>
                    <Textarea
                      id="edit-task-description"
                      value={newTaskDescription}
                      onChange={(e) => setNewTaskDescription(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsEditTaskDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">Update Task</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications" className="space-y-3">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Email Members</CardTitle>
                <CardDescription>Send communications to PTA members</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => { e.preventDefault(); handleSendCommunication(); }} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Recipients</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                      {members.map((member) => (
                        <div key={member.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`email-member-${member.id}`}
                            checked={commRecipients.includes(member.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setCommRecipients([...commRecipients, member.id]);
                              } else {
                                setCommRecipients(commRecipients.filter(id => id !== member.id));
                              }
                            }}
                          />
                          <Label htmlFor={`email-member-${member.id}`} className="text-sm cursor-pointer">
                            {member.full_name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email-subject">Subject</Label>
                    <Input 
                      id="email-subject" 
                      placeholder="Email subject" 
                      value={commSubject}
                      onChange={(e) => setCommSubject(e.target.value)}
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email-message">Message</Label>
                    <Textarea 
                      id="email-message" 
                      placeholder="Type your message here..." 
                      value={commMessage}
                      onChange={(e) => setCommMessage(e.target.value)}
                      rows={8} 
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Attachments</Label>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Input
                          type="file"
                          id="file-attachment"
                          className="flex-1"
                          onChange={(e) => {
                            setCommAttachment(e.target.files?.[0] || null);
                            setCommMeetingAttachment("");
                          }}
                        />
                        {commAttachment && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setCommAttachment(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Or attach meeting agenda:</span>
                        <Select 
                          value={commMeetingAttachment} 
                          onValueChange={(value) => {
                            setCommMeetingAttachment(value === "none" ? "" : value);
                            setCommAttachment(null);
                          }}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Select meeting" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {meetings.map((meeting) => (
                              <SelectItem key={meeting.id} value={meeting.id}>
                                {meeting.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {(commAttachment || commMeetingAttachment) && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Paperclip className="h-3 w-3" />
                          {commAttachment ? commAttachment.name : `Agenda: ${meetings.find(m => m.id === commMeetingAttachment)?.title}`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit">
                      <Send className="h-4 w-4 mr-2" />
                      Send Email
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Minutes Dialog (View Only) */}
          <Dialog open={isMinutesDialogOpen} onOpenChange={(open) => {
            setIsMinutesDialogOpen(open);
            if (!open) {
              setMinutesMeeting(null);
              setSavedMinutesContent(null);
            }
          }}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{minutesMeeting?.title} - Minutes</DialogTitle>
                <DialogDescription>View recorded meeting minutes</DialogDescription>
              </DialogHeader>
              {minutesMeeting && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground">Date & Time</Label>
                    <p>{new Date(minutesMeeting.meeting_date).toLocaleString()}</p>
                  </div>
                  
                  <div className="border-t pt-4">
                    {loadingMinutes ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    ) : savedMinutesContent ? (
                      <div className="space-y-2">
                        <h3 className="font-semibold">Meeting Minutes</h3>
                        <div className="whitespace-pre-wrap bg-muted/50 p-4 rounded-lg text-sm">
                          {savedMinutesContent}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No minutes have been recorded for this meeting yet.
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end border-t pt-4">
                    <Button variant="outline" onClick={() => {
                      setMinutesMeeting(null);
                      setSavedMinutesContent(null);
                      setIsMinutesDialogOpen(false);
                    }}>
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="space-y-3">
            <Card>
              <CardHeader>
                <CardTitle>Alerts</CardTitle>
                <CardDescription>Important alerts and reminders</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {alerts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No alerts</p>
                  ) : (
                    alerts.map((alert) => {
                      const daysUntilDue = Math.ceil((new Date(alert.due_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                      const isCritical = daysUntilDue <= 7 && daysUntilDue >= 0;
                      
                      return (
                        <div key={alert.id} className={`p-3 border rounded-lg ${isCritical ? 'border-destructive bg-destructive/5' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{alert.title}</p>
                              <p className="text-sm text-muted-foreground">Due: {new Date(alert.due_date).toLocaleDateString()}</p>
                              <p className={`text-sm ${isCritical ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                                {daysUntilDue > 0 ? `${daysUntilDue} days remaining` : 'Overdue'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Settings
                </CardTitle>
                <CardDescription>Configure Chair-specific settings and sharing links</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Staff Submission Link */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium">Staff Submission Form</h3>
                    <p className="text-sm text-muted-foreground">
                      Share this link with school staff so they can submit funding requests to the PTA.
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2 mb-4">
                    <Switch
                      id="enable-staff-submissions"
                      checked={enableStaffSubmissions}
                      onCheckedChange={async (checked) => {
                        if (!ptaId) return;
                        const { error } = await supabase
                          .from('ptas')
                          .update({ enable_staff_submissions: checked })
                          .eq('id', ptaId);
                        
                        if (error) {
                          toast({
                            title: "Error",
                            description: "Failed to update settings",
                            variant: "destructive"
                          });
                        } else {
                          setEnableStaffSubmissions(checked);
                          toast({
                            title: "Settings updated",
                            description: checked ? "Staff submissions enabled" : "Staff submissions disabled"
                          });
                        }
                      }}
                    />
                    <Label htmlFor="enable-staff-submissions">Enable staff submissions</Label>
                  </div>

                  {enableStaffSubmissions && (
                    <div className="space-y-3">
                      <Label>Staff Submission Link</Label>
                      <div className="flex gap-2">
                        <Input 
                          value={staffSubmissionLink} 
                          readOnly 
                          className="font-mono text-sm" 
                        />
                        <Button 
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(staffSubmissionLink);
                            toast({
                              title: "Link copied",
                              description: "Staff submission link copied to clipboard"
                            });
                          }}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => window.open(staffSubmissionLink, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        This link allows school staff to submit funding requests. The form includes spam protection.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
